














































































import { Component, Prop, Vue } from 'vue-property-decorator';

import MmStatusBadge from '../../components/MmStatusBadge.vue';
import { MM_EMPTY_DASH } from '../../core/format';
import { MmTask } from '../../types/domain';
import { MmTaskStatus } from '../../types/enums';
import { MmMonitoringBatch, isTaskAwaitingClose, taskHasLoadedResults } from './batch';

const KEY_PREFIX = 'priceMonitoring.mobileMonitoring.results.';

/** Перечень обязан совпадать с тем, по которому считается tasksDone, иначе числа разойдутся */
const DONE_STATUSES: MmTaskStatus[] = [
	MmTaskStatus.COMPLETED,
	MmTaskStatus.ACCEPTED,
	MmTaskStatus.REJECTED,
];

function isTaskDone(task: MmTask): boolean {
	return DONE_STATUSES.indexOf(task.status) !== -1;
}

@Component({
	components: { MmStatusBadge },
})
export default class MonitoringSummary extends Vue {
	@Prop({ default: null }) readonly batch!: MmMonitoringBatch | null;

	@Prop({ default: 0 }) readonly resultsTotal!: number;

	/** Это не resultsTotal: тот про текущую выборку, а этот про мониторинг целиком */
	@Prop({ default: 0 }) readonly batchTotal!: number;

	@Prop({ default: 0 }) readonly acceptedTotal!: number;

	@Prop({ default: 0 }) readonly pendingTotal!: number;

	@Prop({ default: () => ({}) }) readonly resultCountByTaskId!: { [taskId: string]: number };

	@Prop({ default: false }) readonly loading!: boolean;

	get dash(): string {
		return MM_EMPTY_DASH;
	}

	get isVisible(): boolean {
		return this.loading || this.batch !== null;
	}

	get isSkeleton(): boolean {
		return this.loading && this.batch === null;
	}

	get loadingText(): string {
		return this.$t('priceMonitoring.mobileMonitoring.states.loading') as string;
	}

	get labelTasks(): string {
		return this.$t(KEY_PREFIX + 'summaryTasks') as string;
	}

	get labelDone(): string {
		return this.$t(KEY_PREFIX + 'summaryDone') as string;
	}

	get labelResults(): string {
		return this.$t(KEY_PREFIX + 'summaryResults') as string;
	}

	get labelAccepted(): string {
		return this.$t(KEY_PREFIX + 'summaryAccepted') as string;
	}

	get labelPending(): string {
		return this.$t(KEY_PREFIX + 'summaryPending') as string;
	}

	get partialTitleText(): string {
		return this.$t(KEY_PREFIX + 'statePartial', {
			done: this.tasksDone,
			total: this.tasksTotal,
		}) as string;
	}

	get partialHintText(): string {
		if (this.hasAssignedTasksAwaitingClose) {
			return this.$t(KEY_PREFIX + 'statePartialHintWithResults') as string;
		}

		return this.$t(KEY_PREFIX + 'statePartialHint') as string;
	}

	get inProgressTitleText(): string {
		return this.$t(KEY_PREFIX + 'stateInProgress', { count: this.tasksInProgress }) as string;
	}

	get inProgressHintText(): string {
		if (this.hasAssignedTasksAwaitingClose) {
			return this.$t(KEY_PREFIX + 'stateInProgressHintWithResults') as string;
		}

		return this.$t(KEY_PREFIX + 'stateInProgressHint') as string;
	}

	get noResultsTitleText(): string {
		return this.$t(KEY_PREFIX + 'stateTaskNoResults', {
			count: this.tasksWithoutResults.length,
		}) as string;
	}

	get noResultsHintText(): string {
		return this.$t(KEY_PREFIX + 'stateTaskNoResultsHint') as string;
	}

	get allProcessedTitleText(): string {
		return this.$t(KEY_PREFIX + 'stateAllProcessed') as string;
	}

	get allProcessedHintText(): string {
		return this.$t(KEY_PREFIX + 'stateAllProcessedHint') as string;
	}

	get tasksTotal(): number {
		return this.batch === null ? 0 : this.batch.tasksTotal;
	}

	get tasksDone(): number {
		return this.batch === null ? 0 : this.batch.tasksDone;
	}

	get tasksInProgress(): number {
		return this.batch === null ? 0 : this.batch.tasksInProgress;
	}

	get tasksTotalText(): string {
		return this.batch === null ? MM_EMPTY_DASH : this.formatCount(this.tasksTotal);
	}

	get tasksDoneText(): string {
		return this.batch === null ? MM_EMPTY_DASH : this.formatCount(this.tasksDone);
	}

	get resultsText(): string {
		return this.formatCount(this.resultsTotal);
	}

	get acceptedText(): string {
		return this.formatCount(this.acceptedTotal);
	}

	get pendingText(): string {
		return this.formatCount(this.pendingTotal);
	}

	get isPartial(): boolean {
		return this.batch !== null && this.batch.tasksDone < this.batch.tasksTotal;
	}

	get hasTasksInProgress(): boolean {
		return this.tasksInProgress > 0;
	}

	get hasAssignedTasksAwaitingClose(): boolean {
		if (this.batch === null) {
			return false;
		}

		for (let i = 0; i < this.batch.tasks.length; i += 1) {
			if (isTaskAwaitingClose(this.batch.tasks[i])) {
				return true;
			}
		}

		return false;
	}

	get tasksWithoutResults(): MmTask[] {
		if (this.batch === null) {
			return [];
		}

		const result: MmTask[] = [];

		for (let i = 0; i < this.batch.tasks.length; i += 1) {
			const task = this.batch.tasks[i];

			if (!isTaskDone(task)) {
				continue;
			}

			if (taskHasLoadedResults(task.id, this.resultCountByTaskId)) {
				continue;
			}

			if (task.positionsTotal === 0) {
				result.push(task);
			}
		}

		return result;
	}

	get hasTasksWithoutResults(): boolean {
		if (this.loading) {
			return false;
		}

		return this.tasksWithoutResults.length > 0;
	}

	get isAllProcessed(): boolean {
		if (this.batch === null || this.loading) {
			return false;
		}

		return this.safeNumber(this.batchTotal) > 0 && this.safeNumber(this.pendingTotal) === 0;
	}

	private formatCount(value: number): string {
		if (typeof value !== 'number' || isNaN(value)) {
			return MM_EMPTY_DASH;
		}

		return String(value);
	}

	private safeNumber(value: number): number {
		return typeof value === 'number' && !isNaN(value) ? value : 0;
	}
}
